import { type Breadcrumb, useBreadcrumb } from "components/Breadcrumb";
import { PageTitle } from "components/PageTitle";
import { StatusBadge } from "components/ui/StatusBadge";
import { AuthContext } from "hooks/authProvider/authProvider";
import type React from "react";
import { useContext, useEffect, useState } from "react";
import { getCurrentTenant } from "utils/authority-utils";
import { toastPromise } from "utils/toast-utils";

const ROLE_DISPLAY: Record<string, { label: string; badge: string }> = {
  owner: { label: "オーナー", badge: "badge-primary" },
  admin: { label: "管理者", badge: "badge-secondary" },
  member: { label: "メンバー", badge: "badge-ghost" },
};

const BREAD_CRUMBS: Breadcrumb[] = [
  {
    label: "ホーム",
    to: "/",
  },
  {
    label: "プロフィール設定",
  },
];

const ProfilePage = () => {
  const { currentUser, isLoadingUser } = useContext(AuthContext);
  const { setBreadcrumbItems } = useBreadcrumb();

  const [familyName, setFamilyName] = useState("");
  const [givenName, setGivenName] = useState("");
  const [familyNameKana, setFamilyNameKana] = useState("");
  const [givenNameKana, setGivenNameKana] = useState("");
  const [email, setEmail] = useState("");
  const [errormessage, setErrorMessage] = useState(false);

  useEffect(() => {
    setBreadcrumbItems(BREAD_CRUMBS);
  }, [setBreadcrumbItems]);

  useEffect(() => {
    if (currentUser) {
      setFamilyName(currentUser.familyName || "");
      setGivenName(currentUser.givenName || "");
      setFamilyNameKana(currentUser.familyNameKana || "");
      setGivenNameKana(currentUser.givenNameKana || "");
      setEmail(currentUser.email || "");
    }
  }, [currentUser]);

  const onSubmitUpdateUserAttributes = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (
      familyName === "" ||
      givenName === "" ||
      familyNameKana === "" ||
      givenNameKana === ""
    ) {
      setErrorMessage(true);
    } else {
      setErrorMessage(false);

      // トーストを使用して更新処理を実行
      const updateUserPromise = new Promise((resolve, _reject) => {
        // TODO: 実際の更新用のMutationを実装
        setTimeout(() => {
          resolve("更新完了");
        }, 1000);
      });

      await toastPromise(updateUserPromise, {
        pending: "プロフィールを更新中...",
        success: "プロフィールが正常に更新されました",
        error: "プロフィールの更新に失敗しました",
      });
    }
  };

  if (isLoadingUser) {
    return (
      <div>
        <PageTitle>プロフィール設定</PageTitle>
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">読み込み中...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageTitle>プロフィール設定</PageTitle>
      <div className="max-w-2xl mx-auto">
        <div className="bg-white shadow-xl rounded-xl p-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">基本情報</h2>
            <p className="text-gray-600">
              あなたのプロフィール情報を確認・編集できます。
            </p>
          </div>

          <form onSubmit={onSubmitUpdateUserAttributes} className="space-y-6">
            {/* メールアドレス */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                メールアドレス
              </label>
              <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-md">
                {email || "未設定"}
              </div>
            </div>

            {/* テナント・ロール */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                テナント・ロール
              </label>
              <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-md flex items-center gap-2 flex-wrap">
                {(() => {
                  const tenant = getCurrentTenant(currentUser);
                  if (!tenant) return "未所属";
                  const role = ROLE_DISPLAY[tenant.role] || {
                    label: tenant.role,
                    badge: "badge-ghost",
                  };
                  return (
                    <>
                      <span>{tenant.name}</span>
                      <span className={`badge ${role.badge}`}>
                        {role.label}
                      </span>
                    </>
                  );
                })()}
              </div>
              {currentUser?.isSystemAdmin && (
                <div className="mt-2">
                  <StatusBadge tone="soft-warning">システム管理者</StatusBadge>
                </div>
              )}
            </div>

            {/* 氏名 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                氏名
              </label>
              <div className="grid grid-cols-2 gap-4">
                <input
                  value={familyName}
                  onChange={e => setFamilyName(e.target.value)}
                  type="text"
                  placeholder="姓"
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <input
                  value={givenName}
                  onChange={e => setGivenName(e.target.value)}
                  type="text"
                  placeholder="名"
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            {/* ふりがな */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ふりがな
              </label>
              <div className="grid grid-cols-2 gap-4">
                <input
                  value={familyNameKana}
                  onChange={e => setFamilyNameKana(e.target.value)}
                  type="text"
                  placeholder="せい"
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <input
                  value={givenNameKana}
                  onChange={e => setGivenNameKana(e.target.value)}
                  type="text"
                  placeholder="めい"
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            {/* エラーメッセージ */}
            {errormessage && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                氏名またはふりがなを入力してください。
              </div>
            )}

            {/* 保存ボタン */}
            <div className="flex justify-end pt-6">
              <button
                type="submit"
                className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors"
              >
                保存
              </button>
            </div>
          </form>
        </div>

        {/* 開発者向け情報 */}
        <details className="mt-4">
          <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-500">
            開発者向け情報
          </summary>
          <div className="mt-2 space-y-1 text-xs text-gray-400">
            <div>ユーザーID: {currentUser?.userId || "未設定"}</div>
            <div>組織ID: {currentUser?.organizationId || "未設定"}</div>
          </div>
        </details>
      </div>
    </div>
  );
};
export default ProfilePage;
